import { useContext, useMemo } from 'react';
import moment from 'moment';
import { MedicalRecordContext } from 'context';
import { useConversion } from 'hooks';
import { CGM_INTERVAL, GLUCOSE_CONCENTRATION_LEVELS_BOTTOM_VALUES } from 'modules/PatientResults/constants';
const cgmInterval = CGM_INTERVAL * 60;
export const useDayProfileData = (readings)=>{
    const medicalRecord = useContext(MedicalRecordContext);
    const { dataType } = medicalRecord;
    const conversion = useConversion('bloodGlucoseConcentration');
    // TODO: Unify with main thresholds
    const lowThreshold = GLUCOSE_CONCENTRATION_LEVELS_BOTTOM_VALUES[conversion.unit].target;
    const highThreshold = GLUCOSE_CONCENTRATION_LEVELS_BOTTOM_VALUES[conversion.unit].high;
    return useMemo(()=>{
        const high = [];
        const target = [];
        const low = [];
        if (readings) {
            const dataPoints = readings.map(({ value, timestamp })=>{
                const readingMoment = moment.unix(timestamp).utc();
                return {
                    x: readingMoment.hours() + readingMoment.minutes() / 60,
                    y: conversion.toDisplay(value),
                    timestamp
                };
            }).sort((a, b)=>a.x - b.x);
            dataPoints.forEach(({ x, y, timestamp }, idx)=>{
                const nextReading = dataType === 'CGM' ? dataPoints[idx + 1] : null;
                let nextX;
                let nextY;
                let nextTS;
                if (nextReading) {
                    nextX = nextReading.x;
                    nextY = nextReading.y;
                    nextTS = nextReading.timestamp;
                }
                if (y > highThreshold) {
                    high.push({
                        x,
                        y
                    });
                    target.push({
                        x,
                        y: null
                    });
                    low.push({
                        x,
                        y: null
                    });
                    if (nextReading) {
                        if (nextY < lowThreshold) {
                            const diff = Math.round((nextX - x) / 3);
                            high.push({
                                x: x + diff,
                                y: highThreshold
                            });
                            target.push({
                                x: x + diff,
                                y: highThreshold
                            });
                            target.push({
                                x: nextX - diff,
                                y: lowThreshold
                            });
                            low.push({
                                x: nextX - diff,
                                y: lowThreshold
                            });
                        } else if (nextY <= highThreshold) {
                            const diff = Math.round((nextX - x) / 2);
                            high.push({
                                x: x + diff,
                                y: highThreshold
                            });
                            target.push({
                                x: x + diff,
                                y: highThreshold
                            });
                        }
                    }
                } else if (y < lowThreshold) {
                    high.push({
                        x,
                        y: null
                    });
                    target.push({
                        x,
                        y: null
                    });
                    low.push({
                        x,
                        y
                    });
                    if (nextReading) {
                        if (nextY > highThreshold) {
                            const diff = Math.round((nextX - x) / 3);
                            low.push({
                                x: x + diff,
                                y: lowThreshold
                            });
                            target.push({
                                x: x + diff,
                                y: lowThreshold
                            });
                            target.push({
                                x: nextX - diff,
                                y: highThreshold
                            });
                            high.push({
                                x: nextX - diff,
                                y: highThreshold
                            });
                        } else if (nextY >= lowThreshold) {
                            const diff = Math.round((nextX - x) / 2);
                            low.push({
                                x: x + diff,
                                y: lowThreshold
                            });
                            target.push({
                                x: x + diff,
                                y: lowThreshold
                            });
                        }
                    }
                } else {
                    high.push({
                        x,
                        y: null
                    });
                    target.push({
                        x,
                        y
                    });
                    low.push({
                        x,
                        y: null
                    });
                    if (nextReading) {
                        if (nextY < lowThreshold) {
                            const diff = Math.round((nextX - x) / 2);
                            low.push({
                                x: x + diff,
                                y: lowThreshold
                            });
                            target.push({
                                x: x + diff,
                                y: lowThreshold
                            });
                        } else if (nextY > highThreshold) {
                            const diff = Math.round((nextX - x) / 2);
                            high.push({
                                x: x + diff,
                                y: highThreshold
                            });
                            target.push({
                                x: x + diff,
                                y: highThreshold
                            });
                        }
                    }
                }
                if (nextReading) {
                    const xDiff = nextTS - timestamp;
                    if (xDiff > cgmInterval) {
                        const breakTS = timestamp + xDiff / 2;
                        const breakMoment = moment.unix(breakTS).utc();
                        const breakX = breakMoment.hours() + breakMoment.minutes() / 60;
                        high.push({
                            x: breakX,
                            y: null
                        });
                        target.push({
                            x: breakX,
                            y: null
                        });
                        low.push({
                            x: breakX,
                            y: null
                        });
                    }
                }
            });
        }
        return [
            {
                id: 'high',
                data: high
            },
            {
                id: 'target',
                data: target
            },
            {
                id: 'low',
                data: low
            }
        ];
    }, [
        readings,
        conversion
    ]);
};
